import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { motion } from "framer-motion";
import { Send, CheckCircle2, AlertCircle } from "lucide-react";
import { FadeIn } from "./FadeIn";
import { submitContact } from "@/lib/contact.functions";

type Status = "idle" | "submitting" | "success" | "error";

export function ContactForm() {
  const submit = useServerFn(submitContact);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const form = e.currentTarget;
    const fd = new FormData(form);
    const payload = {
      name: String(fd.get("name") ?? "").trim(),
      email: String(fd.get("email") ?? "").trim(),
      message: String(fd.get("message") ?? "").trim(),
    };
    if (!payload.name || !payload.email || !payload.message) {
      setError("Please fill in every field.");
      setStatus("error");
      return;
    }
    setStatus("submitting");
    try {
      await submit({ data: payload });
      setStatus("success");
      form.reset();
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong.");
    }
  }

  return (
    <section id="contact" className="bg-dark text-on-dark py-24 md:py-32">
      <div className="mx-auto max-w-3xl px-6 lg:px-10">
        <FadeIn className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.24em] text-primary mb-3">Get in Touch</p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight">Let's talk</h2>
          <p className="mt-4 text-white/70 max-w-xl mx-auto">
            Have a question, or want to see whether we're a fit? Send a note and I'll get back within
            one business day.
          </p>
        </FadeIn>
        <FadeIn delay={0.1}>
          <form
            onSubmit={onSubmit}
            className="rounded-2xl bg-white/[0.04] border border-white/10 p-6 md:p-8 space-y-5 backdrop-blur-sm"
          >
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Name" name="name" type="text" required maxLength={120} />
              <Field label="Email" name="email" type="email" required maxLength={254} />
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-2" htmlFor="message">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={2000}
                rows={5}
                className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary transition"
                placeholder="Tell me a little about your firm and what you're working on…"
              />
            </div>
            {status === "success" && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 text-sm text-primary"
              >
                <CheckCircle2 className="h-4 w-4" /> Thanks. I'll be in touch soon.
              </motion.p>
            )}
            {status === "error" && error && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 text-sm text-destructive-foreground"
              >
                <AlertCircle className="h-4 w-4" /> {error}
              </motion.p>
            )}
            <div className="flex items-center justify-between gap-4 pt-2">
              <p className="text-xs text-white/40">
                Or email{" "}
                <a href="mailto:katie@katiefernands.com" className="text-white/70 hover:text-primary">
                  katie@katiefernands.com
                </a>
              </p>
              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-medium hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                <Send className="h-4 w-4" />
                {status === "submitting" ? "Sending…" : "Send message"}
              </button>
            </div>
          </form>
        </FadeIn>
      </div>
    </section>
  );
}

function Field({
  label,
  name,
  type,
  required,
  maxLength,
}: {
  label: string;
  name: string;
  type: string;
  required?: boolean;
  maxLength?: number;
}) {
  return (
    <div>
      <label className="block text-sm text-white/70 mb-2" htmlFor={name}>
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        maxLength={maxLength}
        className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary transition"
      />
    </div>
  );
}
